import { create } from 'zustand'
import type { CareGroup, GroupMember, GroupInvitation, CareGroupUpdate } from '@rondela/shared'
import { api, ApiError } from '../../shared/lib/api'

interface GroupState {
  group: CareGroup | null
  members: GroupMember[]
  pendingInvitations: GroupInvitation[]
  isLoading: boolean
  error: string | null

  // Actions
  fetchGroup: () => Promise<void>
  updateGroup: (data: CareGroupUpdate) => Promise<void>
  fetchMembers: () => Promise<void>
  updateMemberRole: (userId: string, role: 'admin' | 'cuidador') => Promise<void>
  removeMember: (userId: string) => Promise<void>
  fetchPendingInvitations: () => Promise<void>
  createInvitation: (email: string, role?: 'admin' | 'cuidador') => Promise<{ devInviteLink?: string }>
  cancelInvitation: (id: string) => Promise<void>
  clearError: () => void
}

export const useGroupStore = create<GroupState>((set, get) => ({
  group: null,
  members: [],
  pendingInvitations: [],
  isLoading: false,
  error: null,

  fetchGroup: async () => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ group: CareGroup }>('/api/groups/current')
      set({ group: response.group, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar grupo'
      set({ error: message, isLoading: false })
    }
  },

  updateGroup: async (data: CareGroupUpdate) => {
    const { group } = get()
    if (!group) return

    try {
      set({ isLoading: true, error: null })
      const response = await api.put<{ group: CareGroup }>(`/api/groups/${group.id}`, data)
      set({ group: response.group, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al actualizar grupo'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  fetchMembers: async () => {
    const { group } = get()
    if (!group) return

    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ members: GroupMember[] }>(`/api/groups/${group.id}/members`)
      set({ members: response.members, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar miembros'
      set({ error: message, isLoading: false })
    }
  },

  updateMemberRole: async (userId: string, role: 'admin' | 'cuidador') => {
    const { group } = get()
    if (!group) return

    try {
      set({ isLoading: true, error: null })
      await api.put(`/api/groups/${group.id}/members/${userId}`, { role })
      set((state) => ({
        members: state.members.map((m) =>
          m.id === userId ? { ...m, role } : m
        ),
        isLoading: false,
      }))
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al actualizar rol'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  removeMember: async (userId: string) => {
    const { group } = get()
    if (!group) return

    try {
      set({ isLoading: true, error: null })
      await api.delete(`/api/groups/${group.id}/members/${userId}`)
      set((state) => ({
        members: state.members.filter((m) => m.id !== userId),
        isLoading: false,
      }))
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al eliminar miembro'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  fetchPendingInvitations: async () => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ invitations: GroupInvitation[] }>('/api/invitations/pending')
      set({ pendingInvitations: response.invitations, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar invitaciones'
      set({ error: message, isLoading: false })
    }
  },

  createInvitation: async (email: string, role: 'admin' | 'cuidador' = 'cuidador') => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.post<{ id: string; message: string; devInviteLink?: string }>(
        '/api/invitations',
        { email, role }
      )
      // Refresh pending invitations
      get().fetchPendingInvitations()
      set({ isLoading: false })
      return { devInviteLink: response.devInviteLink }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al crear invitación'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  cancelInvitation: async (id: string) => {
    try {
      set({ isLoading: true, error: null })
      await api.delete(`/api/invitations/${id}`)
      set((state) => ({
        pendingInvitations: state.pendingInvitations.filter((i) => i.id !== id),
        isLoading: false,
      }))
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cancelar invitación'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  clearError: () => set({ error: null }),
}))
